import { Button } from '@/components/ui/button';
import { AdvisorWarningDialog } from '@/components/AdvisorWarningDialog';
import { OnboardingDownloadReport } from '@/pages/OnboardingDownloadReport';
import { OnboardingOverview } from '@/pages/OnboardingOverview';

import { VscSend } from 'react-icons/vsc';
import { MdDelete } from 'react-icons/md';
import { RiLineChartLine } from 'react-icons/ri';
import { IoDocumentTextOutline } from 'react-icons/io5';
import {
  Trash2,
  Users,
  Link,
  UserPlus,
  CircleUserRound,
  CopyPlus,
  X,
  FileEdit,
} from 'lucide-react';

import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigateToPage } from '@client';
import { useIntakeFormActions } from '@client/hooks/useIntakeFormActions';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

import { SessionType, ClientStatus } from '@shared';
import { selectSession } from '@/model/selectors';
import { ModalState, ERROR_OR_INCOMPLETE_STATUS_TAGS } from '@client';
import { IncompleteIntakeDialog } from '@/shared/components/incomplete-intake-dialog';
import { clientNeedsFullIntakeForm } from '@/shared/lib/utils';

export function ClientActionSheet({
  client,
  sessionAdvisor,
  onClose,
  setModalState,
}: {
  client: Client;
  sessionAdvisor: Advisor;
  onClose: () => void;
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>;
}) {
  const session = useSelector(selectSession);
  const navigateToPage = useNavigateToPage();
  const { navigateToUpdateIntakeForm } = useIntakeFormActions();
  const { flags } = useFeatureFlags();
  const useFormProvider = flags.useFormProvider ?? false;

  const hasFormReference = !!client.formReferenceId;
  const isClientActive = client.clientStatus === ClientStatus.active;
  const showIntakeFormAction = useFormProvider && hasFormReference;
  const buttonText = isClientActive ? 'Update Intake Form' : 'Complete Intake Form';
  const [pendingAction, setPendingAction] = useState<null | (() => void)>(null);
  const [incompleteDialogOpen, setIncompleteDialogOpen] = useState(false);
  const needsFullIntake = clientNeedsFullIntakeForm(client);

  function resolveActionList(client: Client) {
    const hasPartner = !!client.partnerClientId;

    const partnerActions = hasPartner
      ? [
          {
            id: 'edit-partner',
            label: 'Edit Partner',
            icon: Users,
            destructive: false,
            onPress: () => {
              setModalState({ type: 'editPartner' as const, client });
              onClose();
            },
          },
        ]
      : [
          {
            id: 'link-partner',
            label: 'Link Existing Client as Partner',
            icon: Link,
            destructive: false,
            onPress: () => {
              setModalState({ type: 'linkPartner' as const, client });
              onClose();
            },
          },
          {
            id: 'add-partner',
            label: 'Add New Partner',
            icon: UserPlus,
            destructive: false,
            onPress: () => {
              setModalState({ type: 'addPartner' as const, client });
              onClose();
            },
          },
        ];

    const base = [
      {
        id: 'view-results',
        label: 'View Results',
        icon: RiLineChartLine,
        guarded: true,
        destructive: false,
        onPress: () => {
          if (needsFullIntake) {
            setIncompleteDialogOpen(true);
          } else {
            navigateToPage(OnboardingOverview, { clientId: client.clientId });
          }
        },
      },
      ...partnerActions,
      {
        id: 'download-report',
        label: 'Download Report',
        icon: IoDocumentTextOutline,
        guarded: true,
        destructive: false,
        onPress: () =>
          navigateToPage(OnboardingDownloadReport, {
            clientId: client.clientId,
          }),
      },
      {
        id: 'intake-form',
        label: 'Intake Form Data',
        icon: CircleUserRound,
        destructive: false,
        onPress: () => {
          setModalState({ type: 'clientData', client });
          onClose();
        },
      },
      ...(showIntakeFormAction
        ? [
            {
              id: 'update-intake-form',
              label: 'Update Intake Form',
              icon: FileEdit,
              destructive: false,
              onPress: () => {
                setModalState({ type: 'clientDataEdit', client });
                onClose();
              },
            },
          ]
        : []),
      {
        id: 'access-link',
        label: 'Send Access Link',
        icon: VscSend,
        destructive: false,
        onPress: () => {
          setModalState({ type: 'sendClientSigninLink', client });
          onClose();
        },
      },
      ...(session.sessionType === SessionType.wizard
        ? [
            {
              id: 'duplicate',
              label: 'Duplicate Client',
              icon: CopyPlus,
              destructive: false,
              onPress: () => {
                setModalState({ type: 'newDuplicateClient', client });
                onClose();
              },
            },
          ]
        : []),
    ];

    const deleteAction = {
      id: 'delete',
      label: 'Delete Client',
      icon: MdDelete,
      destructive: true,
      onPress: () => {
        setModalState({ type: 'delete', client });
        onClose();
      },
    };

    const hasErrorTag = client.clientTags.some(tag =>
      ERROR_OR_INCOMPLETE_STATUS_TAGS.includes(tag),
    );

    // For error/incomplete clients, show intake form action if applicable, plus delete
    if (hasErrorTag) {
      const errorStateActions = [
        ...(showIntakeFormAction
          ? [
              {
                id: 'update-intake-form',
                label: buttonText,
                icon: FileEdit,
                destructive: false,
                onPress: async () => {
                  await navigateToUpdateIntakeForm(client.clientId);
                  onClose();
                },
              },
            ]
          : []),
        deleteAction,
      ];
      return errorStateActions;
    }

    return [...base, deleteAction];
  }

  const actions = resolveActionList(client);

  function handlePress(action: any) {
    if (action.guarded && sessionAdvisor.advisorId !== client.advisorId) {
      setPendingAction(() => action.onPress);
      return;
    }
    action.onPress();
  }

  return (
    <>
      <div
        className="animate-in fade-in fixed inset-0 z-40 bg-black/40 duration-200"
        onClick={onClose}
      />

      <div className="animate-in slide-in-from-bottom fixed bottom-0 left-0 right-0 z-50 duration-300">
        <div className="border-border mx-auto w-full max-w-md rounded-t-3xl border-t bg-white p-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-950">Actions for</p>
              <h3 className="mt-0.5 text-sm font-semibold text-gray-950">
                {client.clientFirstName} {client.clientLastName}
              </h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-gray-950"
              onClick={onClose}
            >
              <X className="size-4" />
            </Button>
          </div>

          <div className="mb-3 max-h-[60vh] overflow-y-auto">
            {actions.map(action => {
              if (action.destructive) return null;
              const Icon = action.icon;

              return (
                <div key={action.id}>
                  <button
                    onClick={() => handlePress(action)}
                    className="hover:bg-secondary flex w-full items-center gap-3 rounded-lg px-4 py-3.5 text-sm font-medium text-gray-950 transition-colors"
                  >
                    <Icon className="text-primary h-5 w-5 flex-shrink-0" />
                    <span>{action.label}</span>
                  </button>

                  {(action.id === 'edit-partner' || action.id === 'add-partner') && (
                    <div className="border-border/50 my-1 border-b" />
                  )}
                </div>
              );
            })}

            <div className="border-border my-2 border-t" />

            {actions.some(a => a.destructive) && (
              <button
                onClick={() => actions.find(a => a.destructive)?.onPress()}
                className="text-destructive flex w-full items-center gap-3 rounded-lg px-4 py-3.5 text-sm font-medium text-gray-950 transition-colors"
              >
                <Trash2 className="h-5 w-5 flex-shrink-0 text-red-700" />
                <span>Delete Client</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Guard Dialog */}
      {pendingAction && (
        <AdvisorWarningDialog
          title="Viewing another advisor's client"
          message="Warning: Any changes you make will affect the actual client record and be visible to both the client and their primary advisor."
          onContinue={() => {
            pendingAction();
            setPendingAction(null);
          }}
        />
      )}

      <IncompleteIntakeDialog
        open={incompleteDialogOpen}
        onOpenChange={setIncompleteDialogOpen}
        client={client}
      />
    </>
  );
}
